import { getFavoriteArtists, getOnboardingProfile, getRecommendations } from './backendApi';
import { getPreferredCity } from '../hooks/useAvailableCities';
import {
  buildRecommendationEntryFromPreferences,
  withRecommendationFetchLimit
} from './recommendationFilters';
import {
  buildPreferencesFromOnboarding,
  isCompletedValidOnboarding,
  persistOnboardingPreferences,
} from './onboardingProgress';

const fetchFromPreferences = async (preferences) => {
  const city = getPreferredCity(preferences);
  if (!city) return null;

  // Build the API options + URL filter params so the returned list, the URL, and
  // the filter UI agree on a cold login entry (see HomeRedirect).
  const { options: filters, params: urlParams } =
    buildRecommendationEntryFromPreferences(preferences);
  urlParams.set('city', city);

  const recommendations = await getRecommendations(city, withRecommendationFetchLimit(filters));
  return { city, recommendations, filters, search: urlParams.toString() };
};

const fetchFromBackendOnboarding = async () => {
  try {
    const [profileResponse, artistsResponse] = await Promise.all([
      getOnboardingProfile(),
      getFavoriteArtists(),
    ]);
    const profile = profileResponse?.profile;
    const artists = artistsResponse?.artists || [];

    if (!isCompletedValidOnboarding(profile)) return null;

    const preferences = buildPreferencesFromOnboarding(profile, artists);
    const persisted = persistOnboardingPreferences(preferences, { preserveExistingFilters: true });
    return fetchFromPreferences(persisted || preferences);
  } catch (error) {
    console.warn('OAuth recommendations: backend onboarding profile unavailable', error.response?.data || error.message);
    return null;
  }
};

/**
 * Fetch recommendations after OAuth login and return the navigation data
 * that OAuthHandler passes to the event-recommendations page.
 *
 * Reads `user_preferences` from localStorage so it is testable in isolation
 * without the surrounding OAuthHandler component.
 *
 * Returns null when:
 *  - No preferences are saved (new user, no onboarding yet)
 *  - No city can be resolved from preferences or lastSelectedCity
 *  - The API call fails and preferences are corrupted
 */
export const fetchRecommendations = async () => {
  try {
    const userPreferences = localStorage.getItem('user_preferences');
    if (!userPreferences) return await fetchFromBackendOnboarding();

    const preferences = JSON.parse(userPreferences);
    return await fetchFromPreferences(preferences);
  } catch (error) {
    console.error('❌ Failed to fetch recommendations:', error);
    try {
      const prefs = JSON.parse(localStorage.getItem('user_preferences') || '{}');
      const lastCity = getPreferredCity(prefs);
      return lastCity ? { city: lastCity } : null;
    } catch {
      return null;
    }
  }
};
